<script setup lang="ts">
  import { onMounted, ref, computed, watch } from 'vue'
  import {
    CallsService,
    PhoneForwardingRulesService,
    type SuperUserCall as Call,
    type PatchedSuperUserCallRequest,
    type PhoneForwardingRule
  } from '@/types/generated'
  import { useRoute } from 'vue-router'
  import LangfuseIcon from '@/components/icons/LangfuseIcon.vue'
  import VueMarkdown from 'vue-markdown-render'
  import RetellIcon from '@/components/icons/RetellIcon.vue'
  import TwilioIcon from '@/components/icons/TwilioIcon.vue'
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import DownloadIcon from '@/components/icons/DownloadIcon.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import AudioPlaybackButton from '@/components/admin/AudioPlaybackButton.vue'
  import { createSuccessToast, genericErrorToast } from '@/lib/toasts'
  import { CpuChipIcon, UserIcon } from '@heroicons/vue/24/outline'
  import { CheckCircle, XCircle, User, Building2, Landmark } from 'lucide-vue-next'
  import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'
  import { useTranslation } from '@/composables/useTranslation'
  import { Phone, Info } from 'lucide-vue-next'
  import GeminiShadowTab from '@/components/admin/voice/GeminiShadowTab.vue'

  const { t } = useTranslation({
    en: {
      forwardedPhone: 'Call forwarded based on caller phone number',
      forwardedEntity: 'Call forwarded based on entity type detection',
      forwardedText: 'Call forwarded based on text context analysis',
      forwardedRule: 'Call forwarded by rule',
      emailFormatValid: 'Email format is valid and properly formatted',
      emailFormatInvalid:
        'AI extracted this email, but it appears to be invalid. Please check the audio snippet to verify the correct email address.',
      phoneFormatValid: 'Phone number format is valid and properly formatted',
      phoneFormatInvalid:
        'AI extracted this phone number, but it appears to be invalid. Please check the audio snippet to verify the correct phone number.'
    },
    de: {
      forwardedPhone: 'Anruf basierend auf Anrufer-Telefonnummer weitergeleitet',
      forwardedEntity: 'Anruf basierend auf Entitätstyp-Erkennung weitergeleitet',
      forwardedText: 'Anruf basierend auf Textkontext-Analyse weitergeleitet',
      forwardedRule: 'Anruf durch Regel weitergeleitet',
      emailFormatValid: 'E-Mail-Format ist gültig und korrekt formatiert',
      emailFormatInvalid:
        'Die KI hat diese E-Mail-Adresse extrahiert, aber sie scheint ungültig zu sein. Bitte überprüfen Sie das Audio-Snippet, um die korrekte E-Mail-Adresse zu verifizieren.',
      phoneFormatValid: 'Telefonnummer-Format ist gültig und korrekt formatiert',
      phoneFormatInvalid:
        'Die KI hat diese Telefonnummer extrahiert, aber sie scheint ungültig zu sein. Bitte überprüfen Sie das Audio-Snippet, um die korrekte Telefonnummer zu verifizieren.'
    }
  })

  const route = useRoute()
  const audioElement = ref<HTMLAudioElement | null>(null)
  const call = ref<Call | null>(null)
  const emailLink = ref<HTMLAnchorElement | null>(null)
  const isEmailTruncated = ref(false)

  const entityTypeOptions = [
    {
      value: 'natural_person',
      title: 'Natürliche Person',
      icon: User
    },
    {
      value: 'legal_person',
      title: 'Juristische Person',
      icon: Building2
    },
    {
      value: 'institution',
      title: 'Institution',
      icon: Landmark
    }
  ]
  const forwardingRule = ref<PhoneForwardingRule | null>(null)

  const canShowFirstNameRecording = computed(() => {
    return (
      call.value?.audio_recording_url &&
      call.value?.phone_number.can_record_calls &&
      call.value?.result?.first_name_recording_start_seconds != null &&
      call.value?.result?.first_name_recording_end_seconds != null
    )
  })

  const canShowLastNameRecording = computed(() => {
    return (
      call.value?.audio_recording_url &&
      call.value?.phone_number.can_record_calls &&
      call.value?.result?.last_name_recording_start_seconds !== null &&
      call.value?.result?.last_name_recording_end_seconds !== null
    )
  })

  const canShowEmailRecording = computed(() => {
    return (
      call.value?.audio_recording_url &&
      call.value?.phone_number.can_record_calls &&
      call.value?.result?.email_recording_start_seconds !== null &&
      call.value?.result?.email_recording_end_seconds !== null
    )
  })

  const canShowPhoneNumberRecording = computed(() => {
    return (
      call.value?.audio_recording_url &&
      call.value?.phone_number.can_record_calls &&
      call.value?.result?.phone_number_recording_start_seconds !== null &&
      call.value?.result?.phone_number_recording_end_seconds !== null
    )
  })

  watch(
    () => call.value?.audio_recording_url,
    (newUrl) => {
      if (newUrl && audioElement.value) {
        audioElement.value.src = newUrl
        audioElement.value.load()
      }
    }
  )

  const handleAudioSeek = (time: number) => {
    if (audioElement.value) {
      audioElement.value.currentTime = time
      audioElement.value.play()
    }
  }

  const isAudioLoading = ref(false)
  const downloadTranscript = () => {
    const transcriptName = `transcript-${call.value?.uuid}`
    const jsonContent = {
      name: transcriptName,
      transcript: call.value?.transcript
    }
    const fileContent = JSON.stringify(jsonContent, null, 4)
    const blob = new Blob([fileContent], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${transcriptName}.json`
    // Append the anchor to the document body (optional, but required for Firefox)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const checkTruncation = () => {
    if (emailLink.value) {
      isEmailTruncated.value = emailLink.value.scrollWidth > emailLink.value.clientWidth
    }
  }

  const activeTab = ref('overview')
  const ratingFields = {
    agent_rating_by_human: '0',
    system_rating_by_human: '0',
    system_rating_summary_by_human: '',
    was_name_asked: false,
    was_name_mentioned: false,
    is_name_correct: false,
    was_email_asked: false,
    was_email_mentioned: false,
    is_email_correct: false,
    was_phone_asked: false,
    was_phone_mentioned: false,
    is_phone_correct: false,
    needed_to_ask_for_case_number: false,
    was_case_number_asked: false,
    is_case_number_correct: false,
    is_lawfield_correct: false,
    is_lawtopic_correct: false,
    is_classification_correct: false,
    did_caller_hang_up_early: false,
    did_caller_hang_up_early_due_to_conversation: false,
    did_caller_speak_in_single_words: false,
    is_inquiry_helpful: false,
    can_customer_derive_value: false,
    did_call_have_background_noise: false,
    was_caller_annoyed: false,
    had_caller_accent: false
  }

  type RatingFieldsType = typeof ratingFields

  const ratingForm = ref<RatingFieldsType>({ ...ratingFields })

  const loadExistingRatings = () => {
    if (call.value) {
      const keys = Object.keys(ratingFields) as Array<keyof RatingFieldsType>
      keys.forEach((key) => {
        ratingForm.value[key] = call.value[key] ?? ratingFields[key]
      })
    }
  }

  const isRatingAlreadyDone = computed(() => {
    return call.value?.agent_rating_by_human !== null && call.value?.system_rating_by_human !== null
  })

  const isRatingValid = computed(() => {
    return (
      ratingForm.value.agent_rating_by_human != '0' &&
      ratingForm.value.system_rating_by_human != '0'
    )
  })

  const handleSave = async (values: PatchedSuperUserCallRequest) => {
    if (!call.value || !isRatingValid.value) return

    try {
      await CallsService.callsPartialUpdate({
        uuid: call.value.uuid,
        requestBody: values
      })
      createSuccessToast()
      const updatedCall = await CallsService.callsRetrieve({ uuid: call.value.uuid })
      call.value = updatedCall
    } catch (error) {
      console.error('Error saving rating:', error)
      genericErrorToast()
    }
  }

  const dataExtractionFields = [
    // { name: 'name_correct', label: 'Name extracted correctly' },
    // { name: 'email_correct', label: 'Email extracted correctly' },
    // { name: 'phone_correct', label: 'Phone extracted correctly' },
    // { name: 'case_number_correct', label: 'Case number extracted correctly' },
    // { name: 'lawfield_correct', label: 'Law field extracted correctly' },
    // { name: 'lawtopic_correct', label: 'Law topic extracted correctly' },
    // { name: 'classification_correct', label: 'Call classification extracted correctly' }
    // // was_name_asked: null,
    // // was_name_mentioned: null,
    // // is_name_correct: null,
    // // was_email_asked: null,
    // // was_email_mentioned: null,
    // // is_email_correct: null,
    // // was_phone_asked: null,
    // // was_phone_mentioned: null,
    // // is_phone_correct: null,
    // // needed_to_ask_for_case_number: null,
    // // was_case_number_asked: null,
    // // is_case_number_correct: null,
    // // is_lawfield_correct: null,
    // // is_lawtopic_correct: null,
    // // is_classification_correct: null,
    // // did_caller_hang_up_early: null,
    // // did_caller_hang_up_early_due_to_conversation: null,
    // // did_caller_speak_in_single_words: null,
    // // is_inquiry_helpful: null,
    // // can_customer_derive_value: null,
    // // did_call_have_background_noise: null,
    // // was_caller_annoyed: null
    { name: 'was_name_asked', label: 'Asked', cat: 'Name' },
    { name: 'was_name_mentioned', label: 'Mentioned', cat: 'Name' },
    { name: 'is_name_correct', label: 'Correct', cat: 'Name' },

    { name: 'was_email_asked', label: 'Asked', cat: 'Email' },
    { name: 'was_email_mentioned', label: 'Mentioned', cat: 'Email' },
    { name: 'is_email_correct', label: 'Correct', cat: 'Email' },

    { name: 'was_phone_asked', label: 'Asked', cat: 'Phone' },
    { name: 'was_phone_mentioned', label: 'Mentioned', cat: 'Phone' },
    { name: 'is_phone_correct', label: 'Correct', cat: 'Phone' },

    {
      name: 'needed_to_ask_for_case_number',
      label: 'Needed to ask for case number',
      cat: 'Case Number'
    },
    { name: 'was_case_number_asked', label: 'Asked', cat: 'Case Number' },
    { name: 'is_case_number_correct', label: 'Correct', cat: 'Case Number' },

    { name: 'is_lawfield_correct', label: 'Correct', cat: 'Law Field' },
    { name: 'is_lawtopic_correct', label: 'Correct', cat: 'Law Topic' },

    {
      name: 'is_classification_correct',
      label: 'Correct',
      cat: 'Classification'
    },

    { name: 'did_caller_hang_up_early', label: 'Did caller hang up early', cat: 'General' },
    {
      name: 'did_caller_hang_up_early_due_to_conversation',
      label: 'Did caller hang up early due to conversation',
      cat: 'General'
    },
    {
      name: 'did_caller_speak_in_single_words',
      label: 'Did caller speak in single words',
      cat: 'General'
    },
    { name: 'is_inquiry_helpful', label: 'Is inquiry helpful', cat: 'General' },
    { name: 'can_customer_derive_value', label: 'Can customer derive value', cat: 'General' },
    {
      name: 'did_call_have_background_noise',
      label: 'Did call have background noise',
      cat: 'General'
    },
    { name: 'was_caller_annoyed', label: 'Was caller annoyed', cat: 'General' },
    { name: 'had_caller_accent', label: 'Had caller accent', cat: 'General' }
  ]

  const reviewFields = [
    { name: 'agent_rating_by_human', label: 'AI Rating' },
    { name: 'system_rating_by_human', label: 'System Rating' }
  ]

  const getForwardedPhoneNumber = () => {
    if (!call.value?.forward || !('rule_uuid' in (call.value.forward as any))) return ''

    return forwardingRule.value?.target_phone_number || ''
  }

  const getForwardingReason = () => {
    if (!call.value?.forward || !('rule_uuid' in (call.value.forward as any))) return ''
    if (!forwardingRule.value) return 'Forwarding rule applied'

    const rule = forwardingRule.value
    const forwardData = call.value.forward as any
    const title = rule.title || ''
    const reason = forwardData.reason || ''

    if (title && reason) {
      return `${title}: ${reason}`
    } else if (title) {
      return title
    } else if (reason) {
      return reason
    } else {
      // Fallback based on rule type
      const ruleType = rule.rule_type
      switch (ruleType) {
        case 'phonenumber':
          return t('forwardedPhone')
        case 'entity':
          return t('forwardedEntity')
        case 'text':
          return t('forwardedText')
        default:
          return t('forwardedRule')
      }
    }
  }

  onMounted(async () => {
    const callValue = await CallsService.callsRetrieve({ uuid: route.params.uuid.toString() })
    call.value = callValue
    loadExistingRatings()

    if (callValue.forward && 'rule_uuid' in (callValue.forward as any)) {
      try {
        forwardingRule.value = await PhoneForwardingRulesService.phoneForwardingRulesRetrieve({
          uuid: (callValue.forward as any).rule_uuid
        })
      } catch (error) {
        console.error('Error fetching forwarding rule:', error)
      }
    }
  })
</script>
<template>
  <div v-if="call">
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          class="text-wrap lg:text-nowrap"
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Calls', to: ROUTES.ADMIN.VOICE.CALL_LIST },
            {
              title: `Details - ${call.uuid}`,
              to: ROUTES.ADMIN.VOICE.CALL_DETAIL.replace(':uuid', call.uuid)
            }
          ]"
        />
      </div>
    </div>
    <div class="p-4 pt-5">
      <div
        class="flex flex-col xl:rounded-xl xl:border xl:border-gray-200 xl:bg-white xl:p-5 xl:shadow-sm"
      >
        <!-- Grid -->
        <div class="xl:flex">
          <!-- Activity Offcanvas -->
          <div
            id="call-details"
            class="hs-overlay [&amp;::-webkit-scrollbar]:w-2 [&amp;::-webkit-scrollbar-thumb]:rounded-full
              [&amp;::-webkit-scrollbar-track]:bg-gray-100 [&amp;::-webkit-scrollbar-thumb]:bg-gray-300 fixed
              bottom-0 start-0 top-0 z-[60] hidden w-[320px] -translate-x-full transform overflow-y-auto border-e
              border-gray-200 bg-white transition-all duration-300 [--auto-close:xl] hs-overlay-open:translate-x-0
              xl:relative xl:bottom-0 xl:end-auto xl:z-0 xl:block xl:translate-x-0 xl:p-0"
          >
            <!-- Close Button -->
            <div class="xl:hidden">
              <div class="absolute end-4 top-2 z-10">
                <button
                  class="inline-flex size-8 items-center justify-center gap-x-2 rounded-full border border-transparent
                    bg-gray-100 text-gray-800 hover:bg-gray-200 focus:bg-gray-200 focus:outline-none
                    disabled:pointer-events-none disabled:opacity-50"
                  type="button"
                  data-hs-overlay="#call-details"
                >
                  <span class="sr-only">Close</span>
                  <svg
                    class="size-4 flex-shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M18 6 6 18"></path>
                    <path d="m6 6 12 12"></path>
                  </svg>
                </button>
              </div>
            </div>
            <!-- End Close Button -->

            <!-- Body -->
            <div class="mt-3 space-y-5 divide-y divide-gray-200 p-6 xl:pe-4">
              <div class="pt-4 first:pt-0">
                <h2 class="text-sm font-semibold text-gray-800">Law</h2>
                <ul class="mt-3 space-y-2">
                  <li>
                    <div class="inline-flex items-center gap-x-3 text-sm text-gray-800">
                      <svg
                        class="size-4 flex-shrink-0 text-gray-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5"
                        />
                      </svg>
                      {{ call.result?.data.law.field ? call.result?.data.law.field : 'n/a' }}
                      <pre>/</pre>
                      {{ call.result?.data.law.topic ? call.result?.data.law.topic : 'n/a' }}
                    </div>
                  </li>
                </ul>
              </div>

              <div class="pt-4 first:pt-0">
                <h2 class="text-sm font-semibold text-gray-800">Client</h2>

                <!-- List -->
                <ul class="mt-3 space-y-2">
                  <li>
                    <div class="flex items-center justify-between gap-x-3 text-sm text-gray-800">
                      <div class="flex items-center gap-x-3">
                        <component
                          :is="
                            entityTypeOptions.find(
                              (opt) => opt.value === call.result?.data.client.entity_type
                            )?.icon || User
                          "
                          class="size-4 flex-shrink-0 text-gray-600"
                        />
                        <span class="inline-flex items-center gap-2">
                          <span class="font-medium">
                            {{
                              entityTypeOptions.find(
                                (opt) => opt.value === call.result?.data.client.entity_type
                              )?.title || 'Nicht ausgewählt'
                            }}
                          </span>
                          <div
                            v-if="call.result?.data.client.entity_type"
                            class="flex h-4 w-4 items-center justify-center text-gray-600"
                          ></div>
                        </span>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div class="flex items-center justify-between gap-x-3 text-sm text-gray-800">
                      <div class="flex items-center gap-x-3">
                        <svg
                          class="size-4 flex-shrink-0 text-gray-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                          />
                        </svg>
                        <span>{{ call.result?.data.client.first_name }}</span>
                      </div>
                      <AudioPlaybackButton
                        v-if="canShowFirstNameRecording"
                        :audioUrl="call.audio_recording_url ?? ''"
                        :startTime="call.result?.first_name_recording_start_seconds ?? ''"
                        :endTime="call.result?.first_name_recording_end_seconds ?? ''"
                        label="First Name"
                      />
                    </div>
                  </li>
                  <li>
                    <div class="flex items-center justify-between gap-x-3 text-sm text-gray-800">
                      <div class="flex items-center gap-x-3">
                        <svg
                          class="size-4 flex-shrink-0 text-gray-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                          />
                        </svg>
                        <span>{{ call.result?.data.client.last_name }}</span>
                      </div>
                      <AudioPlaybackButton
                        v-if="canShowLastNameRecording"
                        :audioUrl="call.audio_recording_url ?? ''"
                        :startTime="call.result?.last_name_recording_start_seconds ?? ''"
                        :endTime="call.result?.last_name_recording_end_seconds ?? ''"
                        label="Last Name"
                      />
                    </div>
                  </li>
                  <li>
                    <div
                      class="flex flex-wrap items-center justify-between gap-x-3 overflow-hidden text-sm text-gray-800"
                    >
                      <div class="flex items-center gap-x-3 overflow-hidden">
                        <svg
                          class="h-4 w-4 text-gray-600"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <rect
                            width="20"
                            height="16"
                            x="2"
                            y="4"
                            rx="2"
                          />
                          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                        </svg>
                        <span class="inline-flex items-center gap-2 overflow-hidden">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger as-child>
                                <a
                                  ref="emailLink"
                                  class="truncate text-gray-800"
                                  @mouseenter="checkTruncation"
                                >
                                  {{ call.result?.data.client.email }}
                                </a>
                              </TooltipTrigger>
                              <TooltipContent v-if="isEmailTruncated">
                                {{ call.result?.data.client.email }}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <div
                            v-if="
                              call.result?.data.client.is_valid_email !== null &&
                              call.result?.data.client.is_valid_email !== undefined
                            "
                            class="flex h-4 w-4 items-center justify-center"
                          >
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <CheckCircle
                                    v-if="call.result?.data.client.is_valid_email"
                                    class="h-4 w-4 text-green-600"
                                  />
                                  <XCircle
                                    v-else
                                    class="h-4 w-4 text-red-600"
                                  />
                                </TooltipTrigger>
                                <TooltipContent class="max-w-xs">
                                  {{
                                    call.result?.data.client.is_valid_email
                                      ? `${t('emailFormatValid')}`
                                      : `${t('emailFormatInvalid')}`
                                  }}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </span>
                      </div>
                      <div class="flex items-center gap-2">
                        <AudioPlaybackButton
                          v-if="canShowEmailRecording"
                          :audioUrl="call.audio_recording_url ?? ''"
                          :startTime="call.result?.email_recording_start_seconds ?? ''"
                          :endTime="call.result?.email_recording_end_seconds ?? ''"
                          label="Email"
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div
                      class="flex flex-wrap items-center justify-between gap-x-3 overflow-hidden text-sm text-gray-800"
                    >
                      <div class="flex items-center gap-x-3">
                        <svg
                          class="size-4 flex-shrink-0 text-gray-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                          />
                        </svg>
                        <span class="inline-flex items-center gap-2">
                          <a
                            class="text-blue-500"
                            :href="
                              call.result?.data.client.phone
                                ? 'tel:' + call.result?.data.client.phone
                                : ''
                            "
                          >
                            {{ call.result?.data.client.phone }}
                          </a>
                          <div
                            v-if="
                              call.result?.data.client.is_valid_phone_number !== null &&
                              call.result?.data.client.is_valid_phone_number !== undefined
                            "
                            class="flex h-4 w-4 items-center justify-center"
                          >
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <CheckCircle
                                    v-if="call.result?.data.client.is_valid_phone_number"
                                    class="h-4 w-4 text-green-600"
                                  />
                                  <XCircle
                                    v-else
                                    class="h-4 w-4 text-red-600"
                                  />
                                </TooltipTrigger>
                                <TooltipContent class="max-w-xs">
                                  {{
                                    call.result?.data.client.is_valid_phone_number
                                      ? `${t('phoneFormatValid')}`
                                      : `${t('phoneFormatInvalid')}`
                                  }}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </span>
                        <AudioPlaybackButton
                          v-if="canShowPhoneNumberRecording"
                          :audioUrl="call.audio_recording_url ?? ''"
                          :startTime="call.result?.phone_number_recording_start_seconds ?? ''"
                          :endTime="call.result?.phone_number_recording_end_seconds ?? ''"
                          label="Phone Number"
                        />
                      </div>
                    </div>
                  </li>
                </ul>
                <!-- End List -->
              </div>
              <div class="pt-4 first:pt-0">
                <h2 class="text-sm font-semibold text-gray-800">Customer</h2>

                <!-- List -->
                <ul class="mt-3 space-y-2">
                  <li>
                    <div class="inline-flex items-center gap-x-3 text-sm text-gray-800">
                      <svg
                        class="size-4 flex-shrink-0 text-gray-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Zm6-10.125a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0Zm1.294 6.336a6.721 6.721 0 0 1-3.17.789 6.721 6.721 0 0 1-3.168-.789 3.376 3.376 0 0 1 6.338 0Z"
                        />
                      </svg>
                      {{ call.phone_number.org.name }}
                    </div>
                    <!--                    &lt;!&ndash; Button with link to customer &ndash;&gt;-->
                    <!--                    <a href="#" class="text-sm text-blue-600 hover:underline">-->
                    <!--                      View Customer-->
                    <!--                    </a>-->
                  </li>
                </ul>
                <!-- End List -->
              </div>
              <div class="pt-4 first:pt-0">
                <h2 class="text-sm font-semibold text-gray-800">Classification</h2>

                <!-- List -->
                <ul class="mt-3 space-y-2">
                  <li>
                    <div class="inline-flex items-center gap-x-3 text-sm text-gray-800">
                      <pre class="text-nowrap">{{ call.result?.classification }}</pre>
                    </div>
                  </li>
                </ul>
                <!-- End List -->
              </div>

              <!-- Forwarding section - only show when call was forwarded -->
              <div
                v-if="call.forward && 'rule_uuid' in (call.forward as any)"
                class="pt-4 first:pt-0"
              >
                <h2 class="text-sm font-semibold text-gray-800">Forwarding</h2>

                <!-- List -->
                <ul class="mt-3 space-y-2">
                  <li>
                    <div class="flex items-start gap-x-3 text-sm text-gray-800">
                      <Phone class="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-600" />
                      <div class="flex flex-col">
                        <span class="text-gray-600">Forwarded to:</span>
                        <a
                          class="text-blue-500"
                          :href="
                            getForwardedPhoneNumber() ? 'tel:' + getForwardedPhoneNumber() : ''
                          "
                        >
                          {{ getForwardedPhoneNumber() }}
                        </a>
                      </div>
                    </div>
                  </li>
                  <li v-if="getForwardingReason()">
                    <div class="flex items-start gap-x-3 text-sm text-gray-800">
                      <Info class="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-600" />
                      <div class="flex flex-col">
                        <span class="text-gray-600">Reason:</span>
                        <span class="text-gray-800">{{ getForwardingReason() }}</span>
                      </div>
                    </div>
                  </li>
                </ul>
                <!-- End List -->
              </div>

              <div class="pt-4 first:pt-0">
                <h2 class="mb-2 text-sm text-gray-500">Debug</h2>

                <!-- List Group -->
                <ul class="space-y-2">
                  <!-- List Item -->
                  <li>
                    <a
                      class="flex items-center gap-x-3 rounded-xl border border-gray-200 bg-white p-2.5 text-sm font-medium
                        text-gray-800 hover:text-blue-600 focus:bg-gray-100 focus:outline-none"
                      target="_blank"
                      :href="call.langfuse_traces_url"
                    >
                      <span
                        class="flex size-7 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white"
                      >
                        <LangfuseIcon class="size-4 flex-shrink-0" />
                      </span>
                      <div class="grow">
                        <p>Langfuse</p>
                      </div>
                      <svg
                        class="size-4 flex-shrink-0"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="m9 18 6-6-6-6"></path>
                      </svg>
                    </a>
                  </li>
                  <!-- End List Item -->

                  <!-- List Item -->
                  <li>
                    <a
                      class="flex items-center gap-x-3 rounded-xl border border-gray-200 bg-white p-2.5 text-sm font-medium
                        text-gray-800 hover:text-blue-600 focus:bg-gray-100 focus:outline-none"
                      target="_blank"
                      :href="call.retell_dashboard_url"
                    >
                      <span
                        class="flex size-7 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white"
                      >
                        <RetellIcon class="size-4 flex-shrink-0" />
                      </span>
                      <div class="grow">
                        <p>Retell</p>
                      </div>
                      <svg
                        class="size-4 flex-shrink-0"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="m9 18 6-6-6-6"></path>
                      </svg>
                    </a>
                  </li>
                  <!-- End List Item -->

                  <!-- List Item -->
                  <li>
                    <a
                      class="flex items-center gap-x-3 rounded-xl border border-gray-200 bg-white p-2.5 text-sm font-medium
                        text-gray-800 hover:text-blue-600 focus:bg-gray-100 focus:outline-none"
                      target="_blank"
                      :href="call.twilio_console_url"
                    >
                      <span
                        class="flex size-7 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white"
                      >
                        <TwilioIcon class="size-3.5 flex-shrink-0" />
                      </span>
                      <div class="grow">
                        <p>Twilio</p>
                      </div>
                      <svg
                        class="size-4 flex-shrink-0"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="m9 18 6-6-6-6"></path>
                      </svg>
                    </a>
                  </li>
                  <li>
                    <a
                      class="flex items-center gap-x-3 rounded-xl border border-gray-200 bg-white p-2.5 text-sm font-medium
                        text-gray-800 hover:text-blue-600 focus:bg-gray-100 focus:outline-none"
                      target="_blank"
                      href="#"
                      @click.prevent="downloadTranscript"
                    >
                      <span
                        class="flex size-7 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white"
                      >
                        <DownloadIcon class="size-3.5 flex-shrink-0" />
                      </span>
                      <div class="grow">
                        <p>Download transcript</p>
                      </div>
                      <svg
                        class="size-4 flex-shrink-0"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="m9 18 6-6-6-6"></path>
                      </svg>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <!-- End Body -->
          </div>

          <!-- Content -->
          <div class="w-full grow space-y-5 xl:ps-5">
            <!-- Tabs -->
            <div class="mb-4 flex justify-between border-b border-gray-200">
              <ul class="-mb-px flex flex-wrap text-center text-sm font-medium">
                <li
                  class="mr-2"
                  @click="activeTab = 'overview'"
                >
                  <a
                    :class="[
                      'inline-block rounded-t-lg p-4',
                      activeTab === 'overview'
                        ? 'active border-b-2 border-blue-600 text-blue-600'
                        : 'hover:border-gray-300 hover:text-gray-600'
                    ]"
                    href="#"
                  >
                    Overview
                  </a>
                </li>
                <li
                  class="mr-2"
                  @click="activeTab = 'rating'"
                >
                  <a
                    :class="[
                      'inline-block rounded-t-lg p-4',
                      activeTab === 'rating'
                        ? 'active border-b-2 border-blue-600 text-blue-600'
                        : 'hover:border-gray-300 hover:text-gray-600'
                    ]"
                    href="#"
                  >
                    Rating
                  </a>
                </li>
                <li
                  v-if="call.gemini_shadow_comparison"
                  class="mr-2"
                  @click="activeTab = 'gemini'"
                >
                  <a
                    :class="[
                      'inline-block rounded-t-lg p-4',
                      activeTab === 'gemini'
                        ? 'active border-b-2 border-blue-600 text-blue-600'
                        : 'hover:border-gray-300 hover:text-gray-600'
                    ]"
                    href="#"
                  >
                    Gemini
                  </a>
                </li>
              </ul>
              <button
                class="items-center gap-x-2 rounded-lg border border-gray-200 bg-white p-2.5 text-sm font-medium
                  text-gray-800 hover:text-blue-600 focus:bg-gray-100 focus:outline-none xl:hidden"
                type="button"
                data-hs-overlay="#call-details"
              >
                Caller Details
              </button>
            </div>

            <!-- Overview Tab Content -->
            <div v-if="activeTab === 'overview'">
              <!-- Sales Card -->
              <div
                class="flex flex-col space-y-3 rounded-xl border border-gray-200 bg-white p-5 shadow-sm xl:shadow-none"
              >
                <!-- Header -->
                <div class="flex items-center justify-between">
                  <h2 class="inline-block font-semibold text-gray-800">Rating</h2>
                </div>
                <!-- End Header -->

                <!-- Stats Grid -->
                <div class="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                  <!-- Card -->
                  <div class="flex flex-col rounded-xl border border-gray-200 p-4">
                    <h2 class="text-sm text-gray-500">User Rating</h2>

                    <div class="flex items-center gap-x-1.5">
                      <p class="text-xl font-semibold text-gray-800">
                        {{ call.user_feedback?.rating ? call.user_feedback?.rating : '-' }}
                        <span class="text-xs text-gray-500">/ 5</span>
                      </p>
                    </div>
                  </div>
                  <!-- End Card -->

                  <!-- Card -->
                  <div class="flex flex-col rounded-xl border border-gray-200 p-4">
                    <h2 class="text-sm text-gray-500">
                      Agent Rating
                      <button
                        class="ms-1 inline-flex items-center rounded-lg border border-gray-200 bg-white p-1 text-xs font-medium
                          text-gray-800 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
                          disabled:pointer-events-none disabled:opacity-50"
                        type="button"
                      >
                        Change
                      </button>
                    </h2>

                    <div class="flex items-center gap-x-1.5">
                      <p class="text-xl font-semibold text-gray-800">
                        {{ call.agent_rating ? call.agent_rating : '-' }}
                        <span class="text-xs text-gray-500">/ 5</span>
                        <!-- Button to change the rating -->
                      </p>
                    </div>
                  </div>
                  <!-- End Card -->

                  <!-- Card -->
                  <div class="flex flex-col rounded-xl border border-gray-200 p-4">
                    <h2 class="text-sm text-gray-500">System Rating</h2>

                    <div class="flex items-center gap-x-1.5">
                      <p class="text-xl font-semibold text-gray-800">
                        {{ call.system_rating ? call.system_rating : '-' }}
                        <span class="text-xs text-gray-500">/ 5</span>
                      </p>
                    </div>
                  </div>
                  <!-- End Card -->
                </div>
                <!-- End Stats Grid -->

                <!-- Progress -->
                <div class="my-4">
                  <!-- End Progress -->
                </div>
                <!-- End Progress -->
              </div>
              <!-- End Sales Card -->

              <!-- Projects Card -->
              <div
                class="flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm xl:shadow-none"
              >
                <!-- Header -->
                <div class="grid gap-2 p-5 pb-2 sm:flex sm:items-center sm:justify-between">
                  <h2 class="inline-block font-semibold text-gray-800">Recording</h2>
                </div>
                <div class="p-5">
                  <figure v-if="call.audio_recording_url">
                    <audio
                      ref="audioElement"
                      controls
                      :src="call.audio_recording_url"
                      style="width: 100%"
                    ></audio>
                  </figure>
                  <p v-else>No recording available (yet)</p>
                </div>
              </div>
              <!-- End Projects Card -->
              <div
                class="flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm xl:shadow-none"
              >
                <!-- Header -->
                <div class="flex items-center justify-between gap-2 p-5 pb-0">
                  <h2 class="inline-block font-semibold text-gray-800">Inquiry</h2>
                </div>
                <div class="p-5 text-gray-700">
                  <VueMarkdown
                    v-if="call.result?.inquiry"
                    :source="call.result?.inquiry"
                  />
                  <span v-else>n/a</span>
                </div>
              </div>
              <div
                class="flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm xl:shadow-none"
              >
                <div class="flex items-center justify-between gap-2 p-5 pb-0">
                  <h2 class="inline-block font-semibold text-gray-800">Transcript</h2>
                </div>
                <ul class="max-w-full space-y-5 p-5 xl:max-w-[50%]">
                  <li
                    v-for="message in call.transcript"
                    :key="message.content"
                    class="flex max-w-[75%] gap-x-2 sm:gap-x-4"
                    :class="
                      message.role === 'agent' || message.role === 'assistant'
                        ? ''
                        : 'ml-auto flex-row-reverse'
                    "
                  >
                    <!-- Card -->
                    <div
                      v-if="message.role === 'agent' || message.role === 'assistant'"
                      class="flex gap-2 rounded-2xl border border-gray-200 bg-white p-4"
                    >
                      <CpuChipIcon class="h-5 w-5 flex-shrink-0 text-gray-800" />
                      <p class="text-sm text-gray-800">
                        {{ message.content }}
                      </p>
                    </div>
                    <div
                      v-else
                      class="end-0 flex gap-2 rounded-2xl bg-blue-600 p-4 shadow-sm"
                    >
                      <UserIcon class="h-5 w-5 flex-shrink-0 text-white" />
                      <p class="text-sm text-white">
                        {{ message.content }}
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Rating Tab Content -->
            <div
              v-if="activeTab === 'rating'"
              class="space-y-5"
            >
              <!-- Data Extraction Rating Card -->
              <div class="rounded-xl border border-gray-200 bg-white p-5">
                <h2 class="mb-4 text-xl font-semibold text-gray-800">Data Extraction Rating</h2>
                <div class="space-y-2">
                  <div class="flex flex-col justify-start gap-2">
                    <div
                      v-for="category in new Set(dataExtractionFields.map((field) => field.cat))"
                      :key="category"
                    >
                      <div
                        class="flex flex-row gap-8"
                        :class="category === 'General' ? 'items-start' : 'items-center'"
                      >
                        <h3 class="text-md mb-2 font-medium">{{ category }}:</h3>
                        <div
                          class="flex"
                          :class="
                            category === 'General' ? 'flex-col' : 'grow flex-row flex-wrap gap-x-8'
                          "
                        >
                          <CheckboxField
                            v-for="field in dataExtractionFields.filter((f) => f.cat === category)"
                            :key="field.name"
                            v-model="ratingForm[field.name]"
                            :name="field.name"
                            :label="field.label"
                          />
                        </div>
                      </div>
                      <div class="border-b border-gray-200"></div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Review Card -->
              <div class="rounded-xl border border-gray-200 bg-white p-5">
                <h2 class="mb-4 text-xl font-semibold text-gray-800">Review</h2>
                <div class="space-y-6">
                  <div
                    v-for="field in reviewFields"
                    :key="field.name"
                    class="space-y-2"
                  >
                    <label
                      :for="field.name"
                      class="block text-sm font-medium text-gray-700"
                    >
                      {{ field.label }}
                    </label>
                    <div class="flex items-center space-x-4">
                      <input
                        :id="field.name"
                        v-model="ratingForm[field.name]"
                        type="range"
                        min="0"
                        max="5"
                        step="0.1"
                        class="purple-thumb h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 focus:outline-none"
                      />
                      <span class="text-sm font-medium text-gray-700">
                        {{ (ratingForm as any)[field.name] }}
                      </span>
                    </div>
                  </div>
                  <div class="space-y-2">
                    <label
                      for="system_rating_summary_by_human"
                      class="block text-sm font-medium text-gray-700"
                    >
                      System Rating Summary by Human
                    </label>
                    <textarea
                      id="system_rating_summary_by_human"
                      v-model="ratingForm.system_rating_summary_by_human"
                      rows="4"
                      class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500
                        sm:text-sm"
                      placeholder="Enter your review here..."
                    ></textarea>
                  </div>
                </div>
              </div>

              <!-- Save Button -->
              <div class="flex justify-end">
                <button
                  v-if="!isRatingAlreadyDone"
                  :disabled="!isRatingValid"
                  :class="[
                    `inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium
                    text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2`,
                    isRatingValid
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : 'cursor-not-allowed bg-gray-400'
                  ]"
                  @click="handleSave(ratingForm)"
                >
                  Save Review
                </button>
                <p
                  v-else
                  class="text-sm text-gray-600"
                >
                  Rating has already been submitted
                </p>
              </div>
            </div>

            <!-- Gemini Tab Content -->
            <div
              v-if="activeTab === 'gemini' && call.gemini_shadow_comparison"
              class="space-y-5"
            >
              <GeminiShadowTab
                :comparison="call.gemini_shadow_comparison"
                :audio-url="call.audio_recording_url ?? null"
                :audio-element="audioElement"
                :gpt-timestamps="call.result ?? null"
                @seek="handleAudioSeek"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .tab-button {
    @apply rounded-t-lg px-4 py-2 text-sm font-medium transition-colors duration-200;
  }

  .tab-button.active {
    @apply border-l border-r border-t border-gray-200 bg-white text-blue-600;
  }

  .tab-button:not(.active) {
    @apply bg-gray-100 text-gray-500 hover:text-gray-700;
  }

  .purple-thumb::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgb(168 85 247 / var(--tw-bg-opacity));
    cursor: pointer;
  }

  .purple-thumb::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: purple;
    cursor: pointer;
  }
</style>
